import { useCallback, useEffect, useState } from "react"
import { Alert, Avatar, Box, Button, Chip, Stack, Typography } from "@mui/material"
import AccountIcon from "@mui/icons-material/ManageAccountsOutlined"
import CommentIcon from "@mui/icons-material/ChatBubbleOutlineOutlined"
import LoginIcon from "@mui/icons-material/LoginOutlined"
import TaskIcon from "@mui/icons-material/TaskAltOutlined"
import TimeIcon from "@mui/icons-material/ScheduleOutlined"
import WarningIcon from "@mui/icons-material/ReportGmailerrorredOutlined"

import { userApi } from "../../api"
import { LoadingRows } from "../../components/LoadingState"
import { humanDuration } from "../../time/duration"
import { formatDateTime, relativeTime } from "../../utils/format"
import { rise } from "../../motion"

/**
 * What somebody has been doing, as one stream.
 *
 * Task changes, comments, hours, sign-ins and the account's own history all
 * leave a trace, and the question a manager opens this with — "what has
 * Sufiyan been on this week?" or "when was this account last used, and by
 * whom was it changed?" — crosses all of them. The server limits each source
 * to what the viewer could already see, so this never shows anything the rest
 * of the app would not.
 *
 * @param {object} props
 * @param {number} props.userId
 * @param {number} [props.limit] One page. The compact version on the overview
 *   shows one page and a link to the rest.
 * @param {boolean} [props.compact] No filters, no paging.
 * @param {(taskId: number) => void} [props.onOpenTask]
 */

const KINDS = [
  { key: "all", label: "Everything" },
  { key: "task", label: "Tasks" },
  { key: "comment", label: "Comments" },
  { key: "time", label: "Hours" },
  { key: "account", label: "Account" },
  { key: "sign_in", label: "Sign-ins" },
]

/* What a task-history row says, in words. The field names are the server's. */
const FIELD_WORDS = {
  created: "created",
  status_id: "moved",
  assignee: "changed who is on",
  assignee_id: "reassigned",
  priority: "reprioritised",
  due_date: "re-dated",
  title: "renamed",
  description: "edited the description of",
  project_id: "moved to another project",
  parent_task_id: "moved under another task",
  attachment_added: "attached a file to",
  attachment_removed: "removed a file from",
  visible_to_client: "changed who can see",
  labels: "relabelled",
  bulk_update: "updated, with others,",
  copied: "copied",
}

/* Fields whose old and new values are not worth printing. */
const QUIET_FIELDS = new Set(["description", "visible_to_client", "labels", "bulk_update"])

export default function PersonActivity({ userId, limit = 30, compact = false, onlyKinds = null, onOpenTask, onSeeAll }) {
  const [items, setItems] = useState(null)
  const [next, setNext] = useState(null)
  const [includes, setIncludes] = useState(null)
  const [kind, setKind] = useState("all")
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setItems(null)
    setError(null)
    try {
      const data = await userApi.activity(userId, { limit, ...(onlyKinds ? { kinds: onlyKinds.join(",") } : {}) })
      setItems(data.items || [])
      setNext(data.next_before || null)
      setIncludes(data.includes || null)
    } catch (err) {
      setError(err.userMessage || "Their activity could not be loaded.")
      setItems([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `onlyKinds` is a constant at each call site
  }, [userId, limit, onlyKinds?.join(",")])

  useEffect(() => {
    load()
  }, [load])

  const more = async () => {
    if (!next) return
    setLoadingMore(true)
    try {
      const data = await userApi.activity(userId, { limit, before: next, ...(onlyKinds ? { kinds: onlyKinds.join(",") } : {}) })
      setItems((current) => [...current, ...(data.items || [])])
      setNext(data.next_before || null)
    } catch (err) {
      setError(err.userMessage || "Older activity could not be loaded.")
    } finally {
      setLoadingMore(false)
    }
  }

  const shown = (items || []).filter((item) => kind === "all" || item.kind === kind)
  const kinds = KINDS.filter(
    (entry) => (entry.key !== "time" || includes?.time !== false) && (entry.key !== "sign_in" || includes?.sign_ins !== false),
  )

  return (
    <Box>
      {!compact ? (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {kinds.map((entry) => (
            <Chip
              key={entry.key}
              label={entry.label}
              size="small"
              color={kind === entry.key ? "primary" : "default"}
              variant={kind === entry.key ? "filled" : "outlined"}
              onClick={() => setKind(entry.key)}
            />
          ))}
        </Stack>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {items === null ? (
        <LoadingRows rows={compact ? 4 : 6} height={36} />
      ) : shown.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {kind === "all" ? "Nothing recorded yet." : "Nothing of that kind yet."}
        </Typography>
      ) : (
        <Box sx={{ position: "relative" }}>
          {/* The thread down the left, so a column of events reads as a timeline. */}
          <Box sx={{ position: "absolute", left: 15, top: 8, bottom: 8, width: 2, bgcolor: "divider", borderRadius: 1 }} />
          <Stack spacing={1.5}>
            {shown.map((item, index) => (
              <Entry key={item.id} item={item} index={index} onOpenTask={onOpenTask} />
            ))}
          </Stack>
        </Box>
      )}

      {compact && onSeeAll && items && items.length > 0 ? (
        <Button size="small" onClick={onSeeAll} sx={{ mt: 1.5 }}>
          See all activity
        </Button>
      ) : null}
      {!compact && next ? (
        <Button size="small" onClick={more} disabled={loadingMore} sx={{ mt: 2 }}>
          {loadingMore ? "Loading…" : "Older"}
        </Button>
      ) : null}
    </Box>
  )
}

function Entry({ item, index, onOpenTask }) {
  const { icon: Icon, tint, title, detail } = describe(item, onOpenTask)
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ position: "relative", ...rise(index) }}>
      <Avatar sx={{ width: 32, height: 32, bgcolor: tint.bg, color: tint.fg, border: 2, borderColor: "background.paper", flexShrink: 0 }}>
        <Icon sx={{ fontSize: 17 }} />
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1, pt: 0.25 }}>
        <Typography variant="body2" component="div" sx={{ lineHeight: 1.45 }}>
          {title}
        </Typography>
        {detail ? (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25, wordBreak: "break-word" }}>
            {detail}
          </Typography>
        ) : null}
      </Box>
      <Typography variant="caption" color="text.secondary" title={formatDateTime(item.at)} sx={{ flexShrink: 0, pt: 0.4 }}>
        {relativeTime(item.at)}
      </Typography>
    </Stack>
  )
}

/** A task's code and title, opening the task when there is somewhere to open it. */
function TaskRef({ task, onOpenTask }) {
  if (!task) return null
  const text = `${task.code} · ${task.title}`
  if (!onOpenTask) return <strong>{text}</strong>
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpenTask(task.id)}
      sx={{
        appearance: "none",
        border: 0,
        background: "none",
        p: 0,
        font: "inherit",
        fontWeight: 600,
        color: "primary.dark",
        cursor: "pointer",
        textAlign: "left",
        "&:hover": { textDecoration: "underline" },
      }}
    >
      {text}
    </Box>
  )
}

function describe(item, onOpenTask) {
  switch (item.kind) {
    case "task": {
      const verb = FIELD_WORDS[item.field] || `changed the ${String(item.field || "").replace(/_/g, " ")} of`
      const change =
        item.field === "created" || QUIET_FIELDS.has(item.field)
          ? ""
          : item.field === "attachment_added" || item.field === "attachment_removed"
            ? item.to || item.from
              ? `(${item.to || item.from})`
              : ""
            : item.field === "assignee"
              ? item.to
                ? `— added ${item.to}`
                : item.from
                  ? `— removed ${item.from}`
                  : ""
              : item.to
                ? `${item.from ? `from ${item.from} ` : ""}to ${item.to}`
                : ""
      return {
        icon: TaskIcon,
        tint: { bg: "rgba(70,129,31,0.12)", fg: "#3f741c" },
        title: (
          <>
            {item.field === "created" ? "Created " : `${verb.charAt(0).toUpperCase()}${verb.slice(1)} `}
            {item.task?.is_step ? "the step " : ""}
            <TaskRef task={item.task} onOpenTask={onOpenTask} /> {change}
          </>
        ),
      }
    }
    case "comment":
      return {
        icon: CommentIcon,
        tint: { bg: "rgba(47,134,224,0.12)", fg: "#2976c6" },
        title: (
          <>
            Commented on <TaskRef task={item.task} onOpenTask={onOpenTask} />
            {item.internal ? <Chip size="small" label="internal" sx={{ height: 16, fontSize: 10, ml: 0.75 }} /> : null}
          </>
        ),
        detail: `“${item.excerpt}${item.excerpt.length >= 160 ? "…" : ""}”`,
      }
    case "time":
      return {
        icon: TimeIcon,
        tint: { bg: "rgba(233,161,59,0.16)", fg: "#976826" },
        title: (
          <>
            Logged <strong>{humanDuration(item.seconds)}</strong>
            {item.task ? (
              <>
                {" "}
                on <TaskRef task={item.task} onOpenTask={onOpenTask} />
              </>
            ) : item.project ? (
              <> on {item.project}</>
            ) : null}
          </>
        ),
        detail: [item.client, item.description].filter(Boolean).join(" · ") || null,
      }
    case "account":
      return {
        icon: AccountIcon,
        tint: { bg: "rgba(124,77,255,0.12)", fg: "#5e35b1" },
        title: (
          <>
            {item.label}
            {item.actor ? <Typography component="span" variant="body2" color="text.secondary"> by {item.actor}</Typography> : null}
          </>
        ),
        detail: item.detail,
      }
    case "sign_in":
      return item.success
        ? {
            icon: LoginIcon,
            tint: { bg: "action.selected", fg: "text.secondary" },
            title: "Signed in",
            detail: [shortDevice(item.device), item.ip].filter(Boolean).join(" · ") || null,
          }
        : {
            icon: WarningIcon,
            tint: { bg: "rgba(224,75,60,0.12)", fg: "#c64335" },
            title: "A sign-in failed",
            detail: [item.reason ? String(item.reason).replace(/_/g, " ") : null, shortDevice(item.device), item.ip].filter(Boolean).join(" · ") || null,
          }
    default:
      return { icon: AccountIcon, tint: { bg: "action.selected", fg: "text.secondary" }, title: item.kind }
  }
}

/** "Chrome on macOS" out of a user-agent string, or the string itself, trimmed. */
export function shortDevice(agent) {
  const text = String(agent || "")
  if (!text) return null
  const browser = /Edg\//.test(text)
    ? "Edge"
    : /Chrome\//.test(text)
      ? "Chrome"
      : /Firefox\//.test(text)
        ? "Firefox"
        : /Safari\//.test(text)
          ? "Safari"
          : null
  const os = /Windows/.test(text)
    ? "Windows"
    : /Mac OS X|Macintosh/.test(text)
      ? "macOS"
      : /Android/.test(text)
        ? "Android"
        : /iPhone|iPad|iOS/.test(text)
          ? "iOS"
          : /Linux/.test(text)
            ? "Linux"
            : null
  if (browser || os) return [browser, os].filter(Boolean).join(" on ")
  return text.length > 60 ? `${text.slice(0, 57)}…` : text
}
